import React from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";
import emptyFunction from "fbjs/lib/emptyFunction";
import { EventReasons } from "@analytics/enums";

import { useKycLink } from "src/features/kyc/hooks/useKycLink";
import {
  Avatar,
  Button,
  CheckMarkColoredIcon,
  NoDelayIcon,
  Typography,
} from "src/features/kyc/imports/components";
import {
  AvatarSize,
  ButtonSize,
  ButtonVariant,
} from "src/features/kyc/imports/enums";
import {
  useBreakpointMobileLayout,
  useLogout,
  useMyProfile,
  useOpenIntercom,
  useVipInfo,
} from "src/features/kyc/imports/hooks";
import { TYPOGRAPHY_TYPE } from "src/features/kyc/imports/types";

import styles from "./UserSuspendedByUnderageView.scss";

export const UserSuspendedByUnderageView: React.FC = () => {
  const isMobileLayout = useBreakpointMobileLayout();
  const openIntercom = useOpenIntercom();

  const { basicProfile, liveStats } = useMyProfile(false);
  const { fetchKycLinkAndOpen, isKycLinkFetching } = useKycLink();

  const onLogout = useLogout({
    onClose: emptyFunction,
    reason: EventReasons.SELF_LOGOUT,
  });

  const { vipLabel } = useVipInfo({
    vipLevel: liveStats?.vipStatus,
  });

  return (
    <div className={styles.root} data-testid="user-suspended-by-underage-view">
      <div className={styles.wrapper}>
        <Avatar
          size={isMobileLayout ? AvatarSize.MEDIUM_SMALL : AvatarSize.MEDIUM}
          basicProfile={basicProfile}
          vipLabel={vipLabel}
        />
        <Typography type={TYPOGRAPHY_TYPE.HEADLINE1} className={styles.title}>
          <FormattedMessage
            id="account_verification"
            defaultMessage="Account Verification"
          />
        </Typography>
        <Typography
          type={TYPOGRAPHY_TYPE.PARAGRAPH2}
          className={styles.description}
        >
          <FormattedMessage
            id="kyc_verify_age_note"
            defaultMessage="Please verify your age to continue using account"
          />
        </Typography>
        <div className={styles.benefits}>
          <div className={styles.benefit}>
            <div className={styles.icon}>
              <CheckMarkColoredIcon />
            </div>
            <Typography type={TYPOGRAPHY_TYPE.PARAGRAPH2}>
              <FormattedMessage
                id="redeem_confirmation_get_badge"
                defaultMessage="Get official Tango badge"
              />
            </Typography>
          </div>
          <div className={styles.benefit}>
            <div className={styles.icon}>
              <NoDelayIcon />
            </div>
            <Typography type={TYPOGRAPHY_TYPE.PARAGRAPH2}>
              <FormattedMessage
                id="kyc_verify_faster"
                defaultMessage="Get your money faster"
              />
            </Typography>
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            className={styles.action}
            size={ButtonSize.BIG_48}
            variant={ButtonVariant.PRIMARY}
            onClick={fetchKycLinkAndOpen}
            disabled={isKycLinkFetching}
            dataTestId="verify-account-button"
          >
            <Typography type={TYPOGRAPHY_TYPE.HEADLINE4}>
              <FormattedMessage
                id="kyc_verify_account"
                defaultMessage="Verify Account"
              />
            </Typography>
          </Button>
          <Button
            className={styles.action}
            onClick={openIntercom}
            size={ButtonSize.BIG_48}
            variant={ButtonVariant.SECONDARY}
            dataTestId="support-button"
          >
            <FormattedMessage id="suspended.cta" defaultMessage="Support" />
          </Button>
          <Button
            className={classnames(styles.action, styles.signOutAction)}
            onClick={onLogout}
            size={ButtonSize.BIG_48}
            variant={ButtonVariant.SECONDARY}
            dataTestId="sign-out-button"
          >
            <FormattedMessage id="sign-out" defaultMessage="Sign out" />
          </Button>
        </div>
        <Typography
          type={TYPOGRAPHY_TYPE.PARAGRAPH2}
          className={styles.privacy}
        >
          <FormattedMessage
            id="kyc_personal_data_not_shared_note"
            defaultMessage="Your personal data will not be shared outside"
          />
        </Typography>
      </div>
    </div>
  );
};
